import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { BulkImportUpload } from './components/BulkImportUpload';
import { BulkImportSummary } from './components/BulkImportSummary';
import { ImportHistoryPage } from './import-history/ImportHistoryPage';

import {
  useBulkImport,
  type BulkImportCallbacks,
  type BulkImportStateValues,
} from './hooks/useBulkImport';

export interface BulkImportPageProps
  extends Partial<BulkImportCallbacks>,
    Partial<BulkImportStateValues> {
  isProperty?: boolean;
}

// Property-mode callers (the standalone /home/properties/new/import route) sit
// outside a stepper, so they have no parent state to thread up. Stub callbacks
// preserve useBulkImport's contract without forcing those callers to fabricate
// a parent state shape.
const noop = () => {};

export function BulkImportPage({ isProperty = false, ...rest }: BulkImportPageProps) {
  const callbacks: BulkImportCallbacks = {
    setFileId: rest.setFileId ?? noop,
    setAmount: rest.setAmount ?? noop,
    setOrderCountInfo: rest.setOrderCountInfo ?? noop,
    setUploadedByInfo: rest.setUploadedByInfo ?? noop,
    setFileName: rest.setFileName ?? noop,
    setDateInfo: rest.setDateInfo ?? noop,
    setTableData: rest.setTableData ?? noop,
    setSuccessData: rest.setSuccessData ?? noop,
    setFailedData: rest.setFailedData ?? noop,
    setActiveStep: rest.setActiveStep ?? noop,
    setSkippedActiveStep: rest.setSkippedActiveStep,
    setFromTableDataFromNextPage: rest.setFromTableDataFromNextPage ?? noop,
    setFixErrorTableData: rest.setFixErrorTableData ?? noop,
    setSkippedStepFromStepper: rest.setSkippedStepFromStepper,
    setFileState: rest.setFileState ?? noop,
    setFileNumber: rest.setFileNumber ?? noop,
    setShowStep3: rest.setShowStep3 ?? noop,
    setFileUploadDisabled: rest.setFileUploadDisabled ?? noop,
    setIsContinue: rest.setIsContinue ?? noop,
  };

  const stateValues: BulkImportStateValues = {
    fileId: rest.fileId ?? null,
    uploadedByInfo: rest.uploadedByInfo,
    fileName: rest.fileName,
    tableData: rest.tableData ?? null,
    amount: rest.amount,
    orderCountInfo: rest.orderCountInfo,
    successData: rest.successData ?? null,
    failedData: rest.failedData ?? null,
    fromTableDataFromNextPage: rest.fromTableDataFromNextPage ?? false,
    showStep3: rest.showStep3 ?? false,
    fileNumber: rest.fileNumber,
    fileUploadDisabled: rest.fileUploadDisabled ?? false,
    isContinue: rest.isContinue ?? false,
  };

  const hook = useBulkImport({ isProperty, callbacks, stateValues });
  const showImportHistory = hook.showImportHistory();
  const showOrderSummary = !isProperty && stateValues.showStep3;

  return (
    <div className="flex h-full flex-col bg-background">
      {isProperty && (
        <header className="flex shrink-0 items-center gap-3 border-b border-border bg-card px-6 py-4">
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={hook.onCancel}
            tooltip="Back to add property"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Typography as="h1" variant="heading-lg" className="truncate font-medium">
            Bulk Property Import
          </Typography>
        </header>
      )}

      <div className="flex flex-1 flex-col overflow-y-auto">
        <section className="shrink-0 px-6 py-6">
          <BulkImportUpload
            isProperty={isProperty}
            fileName={hook.getFileName()}
            onTemplateDownload={hook.onTemplateDownload}
            onFileUpload={hook.onFileUpload}
            onFileUploadForOrder={hook.onFileUploadForOrder}
            setShowStep3={!isProperty ? callbacks.setShowStep3 : null}
            setFileUploadDisabled={callbacks.setFileUploadDisabled}
            fileUploadDisabled={stateValues.fileUploadDisabled}
            setInProgress={hook.setInProgress}
            inProgress={hook.inProgress}
            setDisabledUploadProperty={hook.setDisabledUploadProperty}
            disabledUploadProperty={hook.disabledUploadProperty}
          />

          {showOrderSummary && (
            <BulkImportSummary
              fileNumber={stateValues.fileNumber}
              fileNameInfo={hook.fileNameInfo}
              successData={stateValues.successData}
              failedCountInfo={hook.failedCountInfo}
              orderTotalValue={hook.orderTotalValue}
              setActiveStep={callbacks.setActiveStep}
            />
          )}
        </section>

        {showImportHistory && (
          // lg+: the min-h floor keeps the history table usable on short
          // laptops, and the section's flex-1 bounds DataTable's internal
          // scroller. Below lg those caps starved that scroller to a sliver
          // once the stacked filters ate the budget (found on-device
          // 2026-08-24), so the section is content-sized there and the page
          // scroller does the scrolling — the card list never inner-scrolls.
          <section className="flex flex-col border-t border-border px-6 pt-6 lg:min-h-[420px] lg:flex-1">
            <Typography as="h2" variant="heading-md" className="mb-4 font-medium">
              Import History
            </Typography>
            <div className="flex min-h-0 flex-1 flex-col">
              <ImportHistoryPage
                apiResponseData={hook.responseData}
                isProperty={isProperty}
                setOrderTotalValue={hook.setOrderTotalValue}
                setFileState={rest.setFileState}
                setFromTableData={rest.setFromTableDataFromNextPage}
                setAmount={rest.setAmount}
                setActiveStep={rest.setActiveStep}
                setFileId={rest.setFileId}
                setUploadedByInfo={rest.setUploadedByInfo}
                setFileName={rest.setFileName}
                setDateInfo={rest.setDateInfo}
                setTableDataInfo={rest.setTableData}
                setSuccessData={rest.setSuccessData}
                setFailedData={rest.setFailedData}
                setOrderCountInfo={rest.setOrderCountInfo}
                setFixErrorTableData={rest.setFixErrorTableData}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
